/**
 * Phase 87av — Éditeur Tiptap riche (gras, italique, souligné, listes, liens).
 *
 * Usage :
 *   <RichTextEditor value={tiptapJson} onChange={(json, plain) => ...} />
 *
 * - `value` : JSON Tiptap (doc structure)
 * - `onChange` : called on every change with (json, plainText)
 */
import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Link2, Heading2, Heading3, Quote, Undo2, Redo2 } from "lucide-react";

const TOOLBAR_BTN = "p-1.5 rounded text-[#1A1C1A] hover:bg-[#F4F4F0] disabled:opacity-30 disabled:cursor-not-allowed transition-colors";
const TOOLBAR_BTN_ACTIVE = "p-1.5 rounded bg-[#1F4E3D] text-white";

export function RichTextEditor({ value, onChange, placeholder = "Rédigez votre courrier ici…", readOnly = false }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        // Phase 87av — StarterKit v3 bundles link/underline ; on les redéfinit ci-dessous
        // avec une config personnalisée, donc on les désactive ici pour éviter le warning
        // 'Duplicate extension names'.
        link: false,
        underline: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https",
        HTMLAttributes: { class: "text-[#1F4E3D] underline" },
      }),
    ],
    content: value && Object.keys(value).length ? value : { type: "doc", content: [{ type: "paragraph" }] },
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      const json = editor.getJSON();
      const plain = editor.getText();
      onChange?.(json, plain);
    },
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none min-h-[300px] p-3 text-sm leading-relaxed",
        "data-testid": "rich-editor-content",
      },
    },
  });

  // Synchronise externe -> éditeur (ex: chargement initial / duplication)
  useEffect(() => {
    if (!editor) return;
    const currentJson = editor.getJSON();
    if (JSON.stringify(currentJson) !== JSON.stringify(value) && value && Object.keys(value).length) {
      editor.commands.setContent(value, false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL du lien :", previousUrl || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="border border-[#E5E5E0] rounded-lg overflow-hidden bg-white" data-testid="rich-editor-root">
      {!readOnly && (
        <div className="flex flex-wrap gap-0.5 px-2 py-1.5 border-b border-[#E5E5E0] bg-[#FAFAF7]">
          <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={editor.isActive("heading", { level: 2 }) ? TOOLBAR_BTN_ACTIVE : TOOLBAR_BTN}
            title="Titre 2" data-testid="rt-heading-2">
            <Heading2 className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className={editor.isActive("heading", { level: 3 }) ? TOOLBAR_BTN_ACTIVE : TOOLBAR_BTN}
            title="Titre 3" data-testid="rt-heading-3">
            <Heading3 className="w-4 h-4" />
          </button>
          <div className="w-px bg-[#E5E5E0] mx-1" />
          <button type="button" onClick={() => editor.chain().focus().toggleBold().run()}
            className={editor.isActive("bold") ? TOOLBAR_BTN_ACTIVE : TOOLBAR_BTN}
            title="Gras (Ctrl+B)" data-testid="rt-bold">
            <Bold className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()}
            className={editor.isActive("italic") ? TOOLBAR_BTN_ACTIVE : TOOLBAR_BTN}
            title="Italique (Ctrl+I)" data-testid="rt-italic">
            <Italic className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={editor.isActive("underline") ? TOOLBAR_BTN_ACTIVE : TOOLBAR_BTN}
            title="Souligné (Ctrl+U)" data-testid="rt-underline">
            <UnderlineIcon className="w-4 h-4" />
          </button>
          <div className="w-px bg-[#E5E5E0] mx-1" />
          <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={editor.isActive("bulletList") ? TOOLBAR_BTN_ACTIVE : TOOLBAR_BTN}
            title="Liste à puces" data-testid="rt-bullet-list">
            <List className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={editor.isActive("orderedList") ? TOOLBAR_BTN_ACTIVE : TOOLBAR_BTN}
            title="Liste numérotée" data-testid="rt-ordered-list">
            <ListOrdered className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={editor.isActive("blockquote") ? TOOLBAR_BTN_ACTIVE : TOOLBAR_BTN}
            title="Citation" data-testid="rt-blockquote">
            <Quote className="w-4 h-4" />
          </button>
          <button type="button" onClick={setLink}
            className={editor.isActive("link") ? TOOLBAR_BTN_ACTIVE : TOOLBAR_BTN}
            title="Insérer un lien" data-testid="rt-link">
            <Link2 className="w-4 h-4" />
          </button>
          <div className="w-px bg-[#E5E5E0] mx-1" />
          <button type="button" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}
            className={TOOLBAR_BTN} title="Annuler (Ctrl+Z)" data-testid="rt-undo">
            <Undo2 className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}
            className={TOOLBAR_BTN} title="Rétablir (Ctrl+Y)" data-testid="rt-redo">
            <Redo2 className="w-4 h-4" />
          </button>
        </div>
      )}
      <EditorContent editor={editor} placeholder={placeholder} />
    </div>
  );
}

export default RichTextEditor;
